//REV-22302 — what the AI is doing with a document, as the hub's row tells it: still working on it, a
//proposal, no proposal (and why), or a file it could not read.
//
//ONE pure function over the row's own fields, shared by the pure view (what to draw in the account
//column) and by the effect (whether to keep re-reading the list): two separate derivations are how a
//table keeps polling for rows that already say "Kein Vorschlag", or stops while one still spins.
//
//Imports TYPES only, like `document-feedback-keys.ts`: the Node guard (`src/tests/document-ai-state.unit.spec.ts`)
//loads it without Angular.

import type {
    AssignmentState,
    BalanceCheck,
    DocumentAccountRef,
    DocumentFiling,
    DocumentProcessingStep,
    DocumentReadingState,
    ProposalSource,
} from '../types/closing-documents.types';
import { READING_STATE_LABEL_KEYS, feedbackKeyOf } from './document-feedback-keys';

/**
 * The four things the account column can say about the AI. `null` = nothing AI-related to say: the
 * row keeps its pre-REV-22302 rendering (chip, Projekt-Akte, or the plain account picker).
 */
export type DocumentAiState = 'working' | 'proposal' | 'noProposal' | 'notReadable';

/**
 * The fields the state is read from. Both the hub's `DocumentRow` and the wire's
 * `IClosingDocumentListItem` satisfy it structurally, which is what lets the effect judge the wire
 * response with the very function the view draws from.
 */
export interface DocumentAiFacts {
    readonly assignment: AssignmentState;
    readonly filing: DocumentFiling;
    readonly notRelevant: boolean;
    readonly account: DocumentAccountRef | null;
    /** ISO-8601 — when the document arrived. The working claim expires `AI_WORKING_WINDOW_MS` after it. */
    readonly addedAt: string;
    readonly readingState?: DocumentReadingState;
    readonly proposalRefusalCode?: string | null;
    readonly proposalRejected?: boolean;
}

/**
 * How long after arrival a document without an AI outcome still counts as "being worked on".
 *
 * The wire carries no "the pipeline is running" flag, only its OUTCOMES (a proposal, a refusal, a
 * failed read). A document that has none of them is either in the pipeline or was never picked up
 * (the organisation switched the mapping off, an old upload from before the AI existed) — and the
 * two look identical on the row. The window is what keeps the first from being claimed forever:
 * after it the row falls back to the plain picker, and the document page still says what the
 * reading state is. Fifteen minutes is well above what a read plus a classification
 * takes; the same ten-minute give-up window the reconciliation poll uses, with margin.
 */
export const AI_WORKING_WINDOW_MS = 15 * 60_000;

/**
 * The state of one row at `now` (ms since epoch). `aiActive` is the organisation's switch for the AI
 * document mapping (the hub's banner reads it): off, no row is ever claimed as "working" — a banner
 * saying "no proposals will come" over a row saying "the AI is looking for an account" is two
 * contradicting truths on one screen.
 *
 * Order matters, and each step is a decision:
 *   1. filed (account or Projekt-Akte) → nothing: the chip says it all;
 *   2. a live proposal → `proposal`, whatever else the row carries;
 *   3. not relevant → nothing: a parked document is not worked on and gets the plain picker, as before;
 *   4. the read was abandoned, or the format is not read at all → `notReadable`;
 *   5. a refusal code, or a human's rejection → `noProposal`;
 *   6. the mapping is off, or the daily cap deferred the read → nothing (the document page says "tomorrow");
 *   7. otherwise `working` while the document is younger than the window — queued, being read, or read
 *      and waiting for the classifier.
 */
export function documentAiStateOf(facts: DocumentAiFacts, now: number, aiActive = true): DocumentAiState | null {
    if (facts.assignment === 'assigned' || facts.filing !== 'unfiled') {
        return null;
    }
    if (facts.assignment === 'proposed' && facts.account != null) {
        return 'proposal';
    }
    if (facts.notRelevant) {
        return null;
    }
    if (facts.readingState === 'failed' || facts.readingState === 'notSupported') {
        return 'notReadable';
    }
    if (hasRefusal(facts) || facts.proposalRejected === true) {
        return 'noProposal';
    }
    if (!aiActive || facts.readingState === 'deferredDailyCap') {
        return null;
    }
    // `Date.parse` of an unreadable date is NaN, and `NaN < window` is false: no claim on a row whose
    // age nobody can tell.
    return now - Date.parse(facts.addedAt) < AI_WORKING_WINDOW_MS ? 'working' : null;
}

function hasRefusal(facts: Pick<DocumentAiFacts, 'proposalRefusalCode'>): boolean {
    return facts.proposalRefusalCode != null && facts.proposalRefusalCode !== '';
}

/** The effect's question: is anything in this list still being worked on, so the list must be re-read? */
export function isAnyDocumentAiWorking(items: readonly DocumentAiFacts[], now: number): boolean {
    return items.some((item) => documentAiStateOf(item, now) === 'working');
}

// ─── Labels ──────────────────────────────────────────────────────────────────────────────────────

/**
 * The classifier's refusal codes, spelled as the AI service spells them (`RefusalCode` in
 * `document-kind-classification.contract.ts`, aumico-ms-ai-agents). Mirrored here like
 * `DOCUMENT_MAPPING_TASK_TYPE`; the lists themselves live in the ABCP kernel. The first six are the
 * account refusals (`ACCOUNT_PROPOSAL_REFUSAL_CODES`), the next four happen BEFORE a Belegart is known
 * (`DOCUMENT_KIND_REFUSAL_CODES`); aumico-be stores and forwards all ten. `REFUSED_NOT_EXTRACTED` is
 * never sent, so no row carries it.
 */
export type DocumentProposalRefusalCode =
    | 'REFUSED_NO_BOUND_ACCOUNT'
    | 'REFUSED_ACCOUNTS_UNCLASSIFIED'
    | 'REFUSED_AMBIGUOUS'
    | 'REFUSED_NO_CANDIDATE'
    | 'REFUSED_NAME_OUT_OF_CUT'
    | 'REFUSED_NAME_UNAVAILABLE'
    | 'REFUSED_UNCLASSIFIED'
    | 'REFUSED_NO_TEXT'
    | 'REFUSED_MODEL_UNPARSEABLE'
    | 'REFUSED_INTEGRITY_FAIL'
    | 'REFUSED_NOT_EXTRACTED';

/** Refusal code → the plain-language reason in the info tooltip. A TOTAL map (the `UPSTREAM_STATE_LABEL_KEYS` construction). */
export const REFUSAL_CODE_LABEL_KEYS: Readonly<Record<DocumentProposalRefusalCode, string>> = {
    REFUSED_NO_BOUND_ACCOUNT: 'abcp_documents_refusal_no_bound_account',
    REFUSED_ACCOUNTS_UNCLASSIFIED: 'abcp_documents_refusal_accounts_unclassified',
    REFUSED_AMBIGUOUS: 'abcp_documents_refusal_ambiguous',
    REFUSED_NO_CANDIDATE: 'abcp_documents_refusal_no_candidate',
    REFUSED_NAME_OUT_OF_CUT: 'abcp_documents_refusal_name_out_of_cut',
    REFUSED_NAME_UNAVAILABLE: 'abcp_documents_refusal_name_unavailable',
    REFUSED_UNCLASSIFIED: 'abcp_documents_refusal_unclassified',
    REFUSED_NO_TEXT: 'abcp_documents_refusal_no_text',
    REFUSED_MODEL_UNPARSEABLE: 'abcp_documents_refusal_model_unparseable',
    REFUSED_INTEGRITY_FAIL: 'abcp_documents_refusal_integrity_fail',
    REFUSED_NOT_EXTRACTED: 'abcp_documents_refusal_not_extracted',
};

/** A code outside the list still gets a sentence — never the raw code, never nothing. */
export const REFUSAL_UNKNOWN_LABEL_KEY = 'abcp_documents_refusal_unknown';
/** The proposal existed and a human rejected it. */
export const PROPOSAL_REJECTED_LABEL_KEY = 'abcp_documents_refusal_rejected';

/**
 * The tooltip of a `noProposal` row. A rejection wins over a code: the backend only sends a code
 * while no proposal state exists, so a row carrying both holds a stale code next to a fresh decision.
 */
export function noProposalReasonKeyOf(
    facts: Pick<DocumentAiFacts, 'proposalRefusalCode' | 'proposalRejected'>,
): string {
    if (facts.proposalRejected === true) {
        return PROPOSAL_REJECTED_LABEL_KEY;
    }
    return feedbackKeyOf(REFUSAL_CODE_LABEL_KEYS, facts.proposalRefusalCode) || REFUSAL_UNKNOWN_LABEL_KEY;
}

/** The tooltip of a `notReadable` row — the REV-22010 reading-state sentence, so both surfaces word it the same. */
export function notReadableReasonKeyOf(facts: Pick<DocumentAiFacts, 'readingState'>): string {
    return feedbackKeyOf(READING_STATE_LABEL_KEYS, facts.readingState);
}

/**
 * The kernel's `DocumentKind` (`@modeso/types__aumico-abcp-kernel-be`), mirrored as wire identifiers
 * for the same reason as the refusal codes above: the twelve Belegarten of Flüssige Mittel,
 * Kurzfristig gehaltene Aktiven mit Börsenkurs and Forderungen aus L+L.
 */
export type DocumentKind =
    | 'BANK_STATEMENT'
    | 'BANK_INTEREST_CAPITAL_STATEMENT'
    | 'BANK_CONFIRMATION'
    | 'BANK_OTHER'
    | 'CASH_CLOSING'
    | 'CASH_IN_TRANSIT_OTHER'
    | 'SECURITIES_DEPOT_STATEMENT'
    | 'SECURITIES_OTHER'
    | 'OPEN_ITEMS_LIST'
    | 'CUSTOMER_BALANCE_CONFIRMATION'
    | 'RECEIVABLES_OTHER'
    | 'ALLOWANCE_CALCULATION';

/** Kind → its label in the Belegtyp column. Total; an unknown kind renders `—`, never its identifier. */
export const DOCUMENT_KIND_LABEL_KEYS: Readonly<Record<DocumentKind, string>> = {
    BANK_STATEMENT: 'abcp_documents_kind_bank_statement',
    BANK_INTEREST_CAPITAL_STATEMENT: 'abcp_documents_kind_bank_interest_capital_statement',
    BANK_CONFIRMATION: 'abcp_documents_kind_bank_confirmation',
    BANK_OTHER: 'abcp_documents_kind_bank_other',
    CASH_CLOSING: 'abcp_documents_kind_cash_closing',
    CASH_IN_TRANSIT_OTHER: 'abcp_documents_kind_cash_in_transit_other',
    SECURITIES_DEPOT_STATEMENT: 'abcp_documents_kind_securities_depot_statement',
    SECURITIES_OTHER: 'abcp_documents_kind_securities_other',
    OPEN_ITEMS_LIST: 'abcp_documents_kind_open_items_list',
    CUSTOMER_BALANCE_CONFIRMATION: 'abcp_documents_kind_customer_balance_confirmation',
    RECEIVABLES_OTHER: 'abcp_documents_kind_receivables_other',
    ALLOWANCE_CALCULATION: 'abcp_documents_kind_allowance_calculation',
};

export function documentKindLabelKeyOf(kind: string | null | undefined): string {
    return feedbackKeyOf(DOCUMENT_KIND_LABEL_KEYS, kind);
}

/** The working indicator's title and one label per real pipeline step. */
export const AI_WORKING_KEYS = {
    title: 'abcp_documents_ai_working',
    steps: {
        SCAN: 'abcp_documents_ai_step_scan',
        TEXT: 'abcp_documents_ai_step_reading',
        KIND: 'abcp_documents_ai_step_kind',
        READING: 'abcp_documents_ai_step_value',
    } as Readonly<Record<DocumentProcessingStep, string>>,
} as const;

export function processingStepLabelKeyOf(step: string | null | undefined): string {
    return feedbackKeyOf(AI_WORKING_KEYS.steps, step);
}

/** Tooltip parts of a candidate chip: where the account came from, and what the balance says. */
export const PROPOSAL_HINT_KEYS = {
    source: {
        CONTENT: 'abcp_documents_proposal_source_content',
        BALANCE: 'abcp_documents_proposal_source_balance',
        BOTH: 'abcp_documents_proposal_source_both',
    } as Readonly<Record<ProposalSource, string>>,
    balance: {
        MATCHES: 'abcp_documents_proposal_balance_matches',
        DIFFERS: 'abcp_documents_proposal_balance_differs',
        NOT_CHECKED: 'abcp_documents_proposal_balance_not_checked',
    } as Readonly<Record<BalanceCheck, string>>,
} as const;

/**
 * The keys of one chip's tooltip. The alternative always comes from the balance strand; the balance
 * verdict is only worth saying for a content pick (a balance pick matches by definition).
 */
export function proposalHintKeysOf(
    facts: { readonly proposalSource?: ProposalSource; readonly balanceCheck?: BalanceCheck },
    alternative: boolean,
): string[] {
    if (alternative) {
        return [PROPOSAL_HINT_KEYS.source.BALANCE];
    }
    if (facts.proposalSource == null) {
        return [];
    }
    const keys = [PROPOSAL_HINT_KEYS.source[facts.proposalSource]];
    if (facts.proposalSource === 'CONTENT' && facts.balanceCheck != null) {
        keys.push(PROPOSAL_HINT_KEYS.balance[facts.balanceCheck]);
    }
    return keys;
}

/** The account column's fixed texts for the two "no outcome" states and their picker action. */
export const AI_STATE_LABEL_KEYS = {
    noProposal: 'abcp_documents_no_proposal',
    notReadable: 'abcp_documents_not_readable',
    choose: 'abcp_documents_account_choose',
} as const;

/**
 * Every key this file can produce except the Belegart labels — what the i18n guard checks against all
 * eight locale files. The Belegart labels are translated in de-CH, de-DE, en and fr only and have their
 * own guard.
 */
export const DOCUMENT_AI_KEYS: readonly string[] = [
    AI_WORKING_KEYS.title,
    ...Object.values(AI_WORKING_KEYS.steps),
    ...Object.values(PROPOSAL_HINT_KEYS.source),
    ...Object.values(PROPOSAL_HINT_KEYS.balance),
    ...Object.values(AI_STATE_LABEL_KEYS),
    ...Object.values(REFUSAL_CODE_LABEL_KEYS),
    REFUSAL_UNKNOWN_LABEL_KEY,
    PROPOSAL_REJECTED_LABEL_KEY,
];
